// ─────────────────────────────────────────────────────────
// layouts/nav/IconRail.tsx — 64px permanent icon rail
//
// Structure (top to bottom):
//   Brand tile (36×36) · Search button (44×44)
//   Main category buttons (48px each, flex:1 area)
//   Footer group: Settings · Super Admin (44px each)
//   Avatar (34×34) — triggers profile popover
// ─────────────────────────────────────────────────────────
import { useRef, useEffect, useState } from 'react'
import { useNavigate }                 from 'react-router-dom'
import { useAuthStore }                from '@/store/authStore'
import { NAV_CATEGORIES, CATEGORY_ICONS } from './navData'
import type { SBIconDef }              from './navData'

// ── Inline SVG renderer for icon path data ────────────────
function SBIcon({ def, size }: { def: SBIconDef; size: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      {def.complex
        ? <g dangerouslySetInnerHTML={{ __html: def.complex }} />
        : <>
            {def.d     && <path d={def.d} />}
            {def.extra && <path d={def.extra} />}
          </>
      }
    </svg>
  )
}

interface IconRailProps {
  activeCategory:   string
  onSelectCategory: (id: string) => void
  badges:           Record<string, number>
  onSearch:         () => void
  onSignOut:        () => void
}

export default function IconRail({
  activeCategory,
  onSelectCategory,
  badges,
  onSearch,
  onSignOut,
}: IconRailProps) {
  const { user, isPlatformAdmin, getRole } = useAuthStore()
  const navigate = useNavigate()
  const [profileOpen, setProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  // Close profile popover on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [profileOpen])

  // User initials
  const initials = (() => {
    const n = user?.displayName || user?.email || 'U'
    const parts = n.trim().split(' ')
    return parts.length > 1
      ? (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
      : n.slice(0, 2).toUpperCase()
  })()

  const role = getRole()

  const mainCats  = NAV_CATEGORIES.filter(c => !c.isFooter)
  const footerCats = NAV_CATEGORIES.filter(c => c.isFooter)

  // ── Active marker bar ─────────────────────────────────
  const ActiveBar = () => (
    <span style={{
      position: 'absolute', left: 0, top: 6, bottom: 6,
      width: 3, borderRadius: '0 3px 3px 0',
      background: 'var(--color-sb-ac)',
    }} />
  )

  // ── Badge pill ────────────────────────────────────────
  const Badge = ({ n }: { n: number }) => {
    if (!n) return null
    return (
      <span style={{
        position: 'absolute', top: 4, right: 4,
        minWidth: 16, height: 16, borderRadius: 99,
        background: 'var(--color-sb-ac)',
        color: 'var(--color-sb-bg)',
        fontFamily: 'var(--font-mono)',
        fontSize: 9.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 4px',
        lineHeight: 1,
      }}>
        {n > 99 ? '99+' : n}
      </span>
    )
  }

  // ── Category button ───────────────────────────────────
  const CatBtn = ({
    id, label, size = 48,
  }: {
    id: string; label: string; size?: number
  }) => {
    const isActive = activeCategory === id
    const icon = CATEGORY_ICONS[id]
    const count = badges[id] ?? 0
    if (!icon) return null
    return (
      <button
        onClick={() => onSelectCategory(id)}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        title={label}
        style={{
          position: 'relative',
          width: '100%', height: size,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isActive ? 'var(--color-sb-active)' : 'transparent',
          border: isActive ? '1px solid rgba(0,216,208,.22)' : '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: isActive ? 'var(--color-sb-ac)' : 'var(--color-sb-tx2)',
          transition: 'background 120ms, color 120ms',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--color-sb-hover)'
        }}
        onMouseLeave={e => {
          if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'
        }}
      >
        {isActive && <ActiveBar />}
        <SBIcon def={icon} size={18} />
        <Badge n={count} />
      </button>
    )
  }

  return (
    <div id="sb-rail" style={{
      width: 64, display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      padding: '10px 10px 0',
      gap: 4, flexShrink: 0,
      borderRight: '1px solid var(--color-sb-bd)',
    }}>

      {/* ── Brand tile ──────────────────────────────── */}
      <button
        onClick={() => navigate('/dashboard')}
        aria-label="CivilBillDesk home"
        title="Dashboard"
        style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: 'var(--color-sb-active)',
          border: '1px solid var(--color-sb-bd)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', marginBottom: 6,
        }}
      >
        <span style={{
          fontSize: 13, fontWeight: 800,
          color: 'var(--color-sb-ac)', letterSpacing: '-.02em',
          fontFamily: 'var(--font-display)',
        }}>CB</span>
      </button>

      {/* ── Search button ────────────────────────────── */}
      <button
        onClick={onSearch}
        aria-label="Search (⌘K)"
        title="Search (⌘K)"
        style={{
          width: 44, height: 44, borderRadius: 8, flexShrink: 0,
          background: 'rgba(0,216,208,.10)',
          border: '1px solid rgba(0,216,208,.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--color-sb-ac)',
          marginBottom: 6,
          transition: 'background 120ms',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,216,208,.18)' }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,216,208,.10)' }}
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
             stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5"/><path d="M15 15l-3.5-3.5"/>
        </svg>
      </button>

      {/* ── Main category buttons ────────────────────── */}
      <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {mainCats.map(cat => (
          <CatBtn key={cat.id} id={cat.id} label={cat.label} size={48} />
        ))}
      </div>

      {/* ── Footer group: Settings + Super Admin ─────── */}
      <div style={{
        width: '100%',
        borderTop: '1px solid var(--color-sb-bd)',
        paddingTop: 8, paddingBottom: 8,
        display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0,
      }}>
        {footerCats.map(cat => {
          if (cat.paOnly && !isPlatformAdmin()) return null
          return <CatBtn key={cat.id} id={cat.id} label={cat.label} size={44} />
        })}
      </div>

      {/* ── Avatar / Profile popover ─────────────────── */}
      <div ref={profileRef} style={{
        position: 'relative', width: '100%',
        display: 'flex', justifyContent: 'center',
        paddingBottom: 12, flexShrink: 0,
      }}>
        {/* Profile popover menu
            position: fixed so it's anchored to viewport, not the 64px rail.
            left: 72 = 64px rail + 8px gap, bottom: 12 = above avatar */}
        {profileOpen && (
          <div style={{
            position: 'fixed', bottom: 12, left: 72,
            width: 200,
            background: 'var(--color-bg)', border: '1px solid var(--color-bd)',
            borderRadius: 8, boxShadow: 'var(--shadow-xl)', zIndex: 400, overflow: 'hidden',
          }}>
            {/* User info header */}
            <div style={{
              padding: '10px 12px 8px',
              borderBottom: '1px solid var(--color-bd)',
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-tx)', marginBottom: 2 }}>
                {user?.displayName || user?.email || '—'}
              </div>
              {role && (
                <div style={{
                  display: 'inline-flex', padding: '1px 5px', borderRadius: 2,
                  fontSize: 7.5, fontWeight: 700, letterSpacing: '.07em',
                  textTransform: 'uppercase',
                  background: role === 'super_admin' ? 'var(--color-neg-bg)' : 'var(--color-blue-bg)',
                  color:      role === 'super_admin' ? 'var(--color-neg)'    : 'var(--color-blue)',
                  border: `1px solid ${role === 'super_admin' ? 'var(--color-neg-bd)' : 'var(--color-blue-bd)'}`,
                }}>{role.replace('_', ' ')}</div>
              )}
            </div>
            <button
              onClick={() => { setProfileOpen(false); navigate('/profile') }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 12px', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 12, color: 'var(--color-tx)',
                textAlign: 'left',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-sf)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none' }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
              </svg>
              My Profile
            </button>
            <div style={{ height: 1, background: 'var(--color-bd)' }} />
            <button
              onClick={() => { setProfileOpen(false); onSignOut() }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 12px', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 12, color: 'var(--color-neg)',
                textAlign: 'left',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--color-sf)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none' }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M6 8h8M11 5l3 3-3 3M6 3H3a1 1 0 00-1 1v8a1 1 0 001 1h3"/>
              </svg>
              Sign Out
            </button>
          </div>
        )}

        {/* Avatar button */}
        <button
          onClick={() => setProfileOpen(o => !o)}
          aria-label={`Account: ${user?.displayName ?? 'Profile'}`}
          aria-expanded={profileOpen}
          title={user?.displayName ?? 'Account'}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'linear-gradient(135deg, #0898A8, var(--color-sb-ac))',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 700,
            letterSpacing: '.04em', flexShrink: 0,
          }}
        >{initials}</button>
      </div>
    </div>
  )
}
